import type { Evidence, IndexDef, LevelCode, ObjectiveEvent, ObjectiveState, Snapshot } from '@/api/types/readiness'
import { formatDate } from '@/components/common/format'
import { levelNum } from '@/api/types/readiness'
import { allObjectives } from '@/lib/indexes/registry'
import { liveLevel, liveScore } from '@/lib/readiness/score'
import { assertScale } from '@/lib/readiness/scale'
import { isLevelMove } from '@/lib/readiness/miniChart'
import { LIVE } from '@/lib/readiness/comparePair'

// ── Compare assessments (Iteration 6 §2, §4) ────────────────────────────────
// The columns of the matrix (one per saved assessment plus Live) and the
// figures of the compared pair, computed once and shared by the Progress page
// and the Progress Report so the band and the matrix never disagree.

export interface CompareColumn {
  key: string
  label: string
  date: string
  snapshot: Snapshot | null
  levels: Record<string, LevelCode>
  scores: Record<string, number | null>
  score: number
  // Live only (§4.3): suggestions where no level has been confirmed. They count
  // toward the live score and show as `L2 ▲ unconfirmed`.
  unconfirmed: Set<string>
  // Live only (§4.3): suggestions flagged on a confirmed level. Not counted in
  // the live score; shown as `L1` with an `L2 suggested` chip.
  suggestedOnConfirmed: Record<string, LevelCode>
  baseline: boolean
  notMigrated: number | null
  known: number | null
  // Column header line 2 (§4.4): the saver, shown when the name is already a date.
  savedBy: string | null
}

export function buildCompareColumns(index: IndexDef, snapshots: Snapshot[], states: Record<string, ObjectiveState>, live: { score: number; notMigrated: number | null; known: number | null }, now = new Date()): CompareColumn[] {
  const items = allObjectives(index)
  const cols: CompareColumn[] = snapshots.map((s) => ({
    key: s.id,
    label: s.name,
    date: s.created_at,
    snapshot: s,
    levels: Object.fromEntries(s.items.map((i) => [i.objective_code, i.level])),
    scores: Object.fromEntries(s.items.map((i) => [i.objective_code, i.score ?? null])),
    score: s.score,
    unconfirmed: new Set<string>(),
    suggestedOnConfirmed: {},
    baseline: s.baseline,
    notMigrated: s.discovery_summary?.assets_not_migrated ?? null,
    known: s.discovery_summary?.assets_total ?? null,
    savedBy: s.created_by === 'system' ? 'System' : s.created_by,
  }))
  const unconfirmed = new Set<string>()
  const suggestedOnConfirmed: Record<string, LevelCode> = {}
  items.forEach(({ objective }) => {
    const st = states[objective.code]
    if (!st) return
    if (st.status === 'suggested') unconfirmed.add(objective.code)
    else if ((st.status === 'confirmed' || st.status === 'declared') && st.suggested_level && st.suggested_level !== st.level) suggestedOnConfirmed[objective.code] = st.suggested_level
  })
  cols.push({
    key: LIVE,
    label: 'Live',
    date: now.toISOString(),
    snapshot: null,
    levels: Object.fromEntries(items.map(({ objective }) => [objective.code, liveLevel(states[objective.code])])),
    scores: Object.fromEntries(items.map(({ objective }) => [objective.code, liveScore(states[objective.code])])),
    score: live.score,
    unconfirmed,
    suggestedOnConfirmed,
    baseline: false,
    notMigrated: live.notMigrated,
    known: live.known,
    savedBy: null,
  })
  return cols
}

export interface CompareFigures {
  delta: (code: string) => number
  movedUp: string[]
  movedDown: string[]
  coverageDown: Set<string>
  // Moves that rely on an unconfirmed suggestion (§2.1, §4.2).
  unconfirmedMoves: Set<string>
  evidenceCount: number
  stillL0: string[]
  scoreDelta: number
}

export function compareFigures(index: IndexDef, events: ObjectiveEvent[], evidence: Evidence[], from: CompareColumn, to: CompareColumn): CompareFigures {
  const scale = assertScale(index)
  const items = allObjectives(index)
  const delta = (code: string) => (scale === 'levels' ? levelNum(to.levels[code] ?? 'L0') - levelNum(from.levels[code] ?? 'L0') : (to.scores[code] ?? 0) - (from.scores[code] ?? 0))
  const movedUp = items.filter(({ objective }) => delta(objective.code) > 0).map((x) => x.objective.code)
  const downAll = items.filter(({ objective }) => delta(objective.code) < 0).map((x) => x.objective.code)
  // A drop whose latest downward event in the window is coverage-driven is shown apart (I3 §4.1).
  const coverageDown = new Set(downAll.filter((c) => {
    const d = [...events].filter((e) => e.objective_code === c && e.occurred_at > from.date && e.occurred_at <= to.date && e.from_level && e.to_level && levelNum(e.to_level) < levelNum(e.from_level)).pop()
    return d?.cause === 'coverage'
  }))
  const movedDown = downAll.filter((c) => !coverageDown.has(c))
  const unconfirmedMoves = new Set(movedUp.filter((c) => to.key === LIVE && to.unconfirmed.has(c)))
  const evidenceCount = evidence.filter((e) => !e.archived_at && new Date(e.occurred_at) > new Date(from.date) && new Date(e.occurred_at) <= new Date(to.date)).length
  const stillL0 = items.filter(({ objective }) => (scale === 'levels' ? (to.levels[objective.code] ?? 'L0') === 'L0' : (to.scores[objective.code] ?? null) == null)).map((x) => x.objective.code)
  const scoreDelta = Math.round((to.score - from.score) * 10) / 10
  return { delta, movedUp, movedDown, coverageDown, unconfirmedMoves, evidenceCount, stillL0, scoreDelta }
}

// When the level shown in a column was last reached (§4.2 `Same since`): the
// last confirmed level move at or before the column's date, else tracking
// start (the first readiness event) for an objective that never moved.
export function levelReachedAt(events: ObjectiveEvent[], code: string, atIso: string): string | null {
  const sorted = [...events].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
  const last = sorted.filter((e) => e.objective_code === code && isLevelMove(e) && e.occurred_at <= atIso).pop()
  return last?.occurred_at ?? sorted[0]?.occurred_at ?? null
}

// Column header line 2 (§4.4): the date, unless the assessment name already is
// one (scheduled saves are named by date), then the saver.
export function looksLikeDate(name: string): boolean {
  return /\d/.test(name) && !Number.isNaN(Date.parse(name))
}

export const fmtMonthYear = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '—')

// ── Matrix columns (I6 fix: no horizontal scroll) ───────────────────────────
// At most five assessment columns: From, To, and up to three context columns
// chosen in this order: saved assessments between From and To, most recent
// first; then those immediately before From; if fewer exist, what there is.
// Columns after To (including Live when To is a saved assessment) are never
// context. The result keeps the chronological order of `columns`.

export const MAX_MATRIX_COLUMNS = 5

export function pickMatrixColumns(columns: CompareColumn[], fromKey: string, toKey: string, maxContext = MAX_MATRIX_COLUMNS - 2): CompareColumn[] {
  const fi = Math.max(0, columns.findIndex((c) => c.key === fromKey))
  const ti = Math.max(0, columns.findIndex((c) => c.key === toKey))
  const lo = Math.min(fi, ti)
  const hi = Math.max(fi, ti)
  const keep = new Set<number>([lo, hi])
  const limit = keep.size + Math.max(0, Math.min(MAX_MATRIX_COLUMNS - 2, maxContext))
  const room = () => limit - keep.size
  for (let i = hi - 1; i > lo && room() > 0; i--) keep.add(i)
  for (let i = lo - 1; i >= 0 && room() > 0; i--) keep.add(i)
  return columns.filter((_, i) => keep.has(i))
}

// Rail and View all rows (I6 fix): the saver, and a second line that never
// repeats a date the assessment name already carries.
export const savedBy = (s: Snapshot) => (s.created_by === 'system' ? 'System' : s.created_by)

export function savedLine2(s: Snapshot): string {
  if (looksLikeDate(s.name)) return s.created_by === 'system' ? 'System · quarterly reminder' : s.created_by
  return `${savedBy(s)} · ${formatDate(s.created_at)}`
}
